import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { FormControl, Input, Select, SimpleGrid, Textarea, VStack } from "@chakra-ui/react";
import moment from "moment";
import { FiCalendar, FiFileText, FiUser } from "react-icons/fi";

import AssigneeSelect from "./AssigneeSelect";
import {
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyModalShell,
	usePropertyModalTheme,
} from "../PropertyModal/shared";
import { createAgencyTaskAsync, listAgencyTaskAssigneesAsync, updateAgencyTaskAsync } from "../../redux/tasks/tasksSlice";
import { resolveAssigneeValue, suggestionOptions } from "../../utils/taskAssignees";

const STATUS_OPTIONS = ["open", "inProgress", "done", "cancelled"];

const emptyForm = {
	title: "",
	description: "",
	assignee: "",
	dueDate: moment().format("YYYY-MM-DD"),
	status: "open",
};

export default function TaskFormModal({ isOpen, onClose, task, onSaved }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { borderColor } = usePropertyModalTheme();
	const assignees = useSelector((state) => state.tasks.assignees);
	const assigneesStatus = useSelector((state) => state.tasks.assigneesStatus);
	const mutationStatus = useSelector((state) => state.tasks.mutationStatus);
	const [form, setForm] = useState(emptyForm);
	const [error, setError] = useState({});
	const isEdit = Boolean(task?.id);

	useEffect(() => {
		if (!isOpen) return;
		dispatch(listAgencyTaskAssigneesAsync());
		if (task) {
			setForm({
				title: task.title || "",
				description: task.description || "",
				assignee: task.assignee || "",
				dueDate: task.dueDate || moment().format("YYYY-MM-DD"),
				status: task.status || "open",
			});
		} else {
			setForm(emptyForm);
		}
		setError({});
	}, [isOpen, task, dispatch]);

	const options = useMemo(() => suggestionOptions(assignees), [assignees]);
	const selectedOption = form.assignee ? { label: form.assignee, value: form.assignee } : null;

	const handleChange = (e) => {
		setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
	};

	const handleAssigneeChange = (option) => {
		const raw = option?.value || option?.label || "";
		setForm((prev) => ({ ...prev, assignee: resolveAssigneeValue(assignees, raw) }));
	};

	const handleCreateAssignee = (inputValue) => {
		setForm((prev) => ({ ...prev, assignee: resolveAssigneeValue(assignees, inputValue) }));
	};

	const save = () => {
		const nextErrors = {};
		if (!form.title.trim()) nextErrors.title = true;
		if (!form.assignee.trim()) nextErrors.assignee = true;
		if (!form.dueDate) nextErrors.dueDate = true;
		if (Object.keys(nextErrors).length) {
			setError(nextErrors);
			return;
		}
		setError({});

		const payload = {
			title: form.title.trim(),
			description: form.description.trim(),
			assignee: resolveAssigneeValue(assignees, form.assignee),
			dueDate: form.dueDate,
			status: form.status,
		};

		const action = isEdit ? updateAgencyTaskAsync({ taskId: task.id, ...payload }) : createAgencyTaskAsync(payload);

		dispatch(action).then((result) => {
			if (result.error) return;
			onSaved?.(result.payload?.task);
			onClose();
		});
	};

	return (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={onClose}
			size="xl"
			icon={FiFileText}
			title={isEdit ? t("tasks.editTask") : t("tasks.addTask")}
			subtitle={t("tasks.taskFormSubtitle")}
			footer={<ModalFooterActions isLoading={mutationStatus === "loading"} onCancel={onClose} onConfirm={save} confirmLabel={t("misc.save")} />}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<VStack spacing={3} align="stretch">
					<FormControl isInvalid={error.title}>
						<ModalFieldLabel icon={FiFileText}>{t("tasks.title")}</ModalFieldLabel>
						<Input name="title" value={form.title} onChange={handleChange} borderRadius="md" />
					</FormControl>
					<FormControl>
						<ModalFieldLabel icon={FiFileText}>{t("tasks.description")}</ModalFieldLabel>
						<Textarea name="description" value={form.description} onChange={handleChange} rows={4} borderRadius="md" />
					</FormControl>
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
						<FormControl isInvalid={error.assignee}>
							<ModalFieldLabel icon={FiUser}>{t("tasks.assignee")}</ModalFieldLabel>
							<AssigneeSelect
								isLoading={assigneesStatus === "loading"}
								isInvalid={Boolean(error.assignee)}
								borderColor={borderColor}
								options={options}
								value={selectedOption}
								onChange={handleAssigneeChange}
								onCreateOption={handleCreateAssignee}
								placeholder={t("tasks.assigneePlaceholder")}
								formatCreateLabel={(input) => t("tasks.createAssignee", { name: input })}
								noOptionsMessage={() => t("tasks.noAssignees")}
								menuPortalTarget={typeof document !== "undefined" ? document.body : null}
								menuPosition="fixed"
								styles={{
									menuPortal: (provided) => ({ ...provided, zIndex: 2000 }),
								}}
							/>
						</FormControl>
						<FormControl isInvalid={error.dueDate}>
							<ModalFieldLabel icon={FiCalendar}>{t("tasks.dueDate")}</ModalFieldLabel>
							<Input type="date" name="dueDate" value={form.dueDate} onChange={handleChange} borderRadius="md" />
						</FormControl>
					</SimpleGrid>
					{isEdit && (
						<FormControl>
							<ModalFieldLabel icon={FiFileText}>{t("tasks.status")}</ModalFieldLabel>
							<Select name="status" value={form.status} onChange={handleChange} borderRadius="md">
								{STATUS_OPTIONS.map((status) => (
									<option key={status} value={status}>
										{t(`tasks.statusValues.${status}`)}
									</option>
								))}
							</Select>
						</FormControl>
					)}
				</VStack>
			</VStack>
		</PropertyModalShell>
	);
}
